import { buttonVariants } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';

import {
  statusIconColors,
  type statusIconVariants,
} from '@/components/ui/session-status/status.variants';
import { SessionUrgencyFlag } from '@/components/ui/session-urgency/session-urgency-flag';
import { isSessionUrgent } from '@/components/ui/session-urgency/types';
import {
  useConsolidatedSessionSelection,
  useSessionSelectedValue,
} from '@/features/consolidatedSessionsList/hooks/useConsolidatedSessionSelection';
import { type ConsolidatedSession } from '@/features/consolidatedSessionsList/types';
import { getSessionUniqueId } from '@/features/consolidatedSessionsList/utils/getSessionUniqueId';
import { OfflineSyncIndicator } from '@/features/sessions/components/session-list/offline-sync-indicator';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useSessionDateDisplay } from '@/hooks/useSessionDateDisplay';
import { cn } from '@/lib/cn';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { SessionStatus } from '@heidi/schema/ml-scribe';
import { statusNameMessages } from '@heidi/session';
import { Circle } from 'lucide-react';
import { useRouter } from 'next/router';
import { Fragment, memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { consolidatedSessionsListMessages } from '../consolidatedSessionsList.messages';
import { ConsolidatedSessionLinkAvatar } from './consolidatedSessionLinkAvatar';

interface ConsolidatedSessionListItemProps {
  session: ConsolidatedSession;
  index: number;
  prevSession?: ConsolidatedSession | null;
  nextSession?: ConsolidatedSession | null;
  onSessionClick: (session: ConsolidatedSession) => void;
}

/**
 * Session list item component for the consolidated sessions list.
 * Layout: [session date time] [patient profile icon + session name] [session status dot / checkbox]
 */
export const ConsolidatedSessionListItem = memo(
  ({
    session,
    index,
    prevSession,
    nextSession,
    onSessionClick,
  }: ConsolidatedSessionListItemProps) => {
    const intl = useIntl();
    const { isTogetherOrHigherTier } = useQueryUserTier();
    const { displaySessionTime, displayFormattedDate, displayDayRelative } =
      useSessionDateDisplay();
    const router = useRouter();

    const sessionUniqueId = getSessionUniqueId(session) ?? '';
    const { isSelected, setSelected } = useConsolidatedSessionSelection(sessionUniqueId);

    // Neighbor selection reads for adjacency joining
    const prevSessionId = prevSession ? (getSessionUniqueId(prevSession) ?? '') : '';
    const nextSessionId = nextSession ? (getSessionUniqueId(nextSession) ?? '') : '';
    const isPrevSelected = useSessionSelectedValue(prevSessionId);
    const isNextSelected = useSessionSelectedValue(nextSessionId);

    const isSessionLinked = useMemo(
      () => session.isFromEhr || !!session.patient_profile,
      [session.isFromEhr, session.patient_profile]
    );

    const patientInitials = useMemo(() => {
      const name = session.displayName?.trim();
      if (!name) {
        return undefined;
      }

      const parts = name.split(/\s+/);
      if (parts.length >= 2) {
        const firstInitial = parts.at(0)?.charAt(0) ?? '';
        const secondInitial = parts.at(1)?.charAt(0) ?? '';
        return `${firstInitial}${secondInitial}`.toUpperCase();
      }
      return name.charAt(0).toUpperCase();
    }, [session.displayName]);

    // Date header logic
    const currentDate = useMemo(
      () => displayFormattedDate(session.created_time_local, true),
      [session.created_time_local, displayFormattedDate]
    );
    const prevDate = useMemo(
      () =>
        prevSession ? displayFormattedDate(prevSession.created_time_local, true) : null,
      [prevSession, displayFormattedDate]
    );
    const showDateHeader = useMemo(
      () => index === 0 || currentDate !== prevDate,
      [index, currentDate, prevDate]
    );

    // Get the relative date display (Today, Yesterday, etc.)
    const dateHeaderText = useMemo(
      () => displayDayRelative(session.created_time_local, true),
      [session.created_time_local, displayDayRelative]
    );

    // Session status for display
    const sessionStatus = session.session_status ?? SessionStatus.EMPTY;
    const statusKey = sessionStatus.toLowerCase() as keyof typeof statusIconVariants;

    const isCurrentSession =
      session.session_id && router.asPath.includes(session.session_id);

    // Adjacency joining: determine whether to visually merge with prev/next selected items
    const isPrevCurrentSession =
      prevSession?.session_id && router.asPath.includes(prevSession.session_id);
    const isNextCurrentSession =
      nextSession?.session_id && router.asPath.includes(nextSession.session_id);

    const nextDate = useMemo(
      () =>
        nextSession ? displayFormattedDate(nextSession.created_time_local, true) : null,
      [nextSession, displayFormattedDate]
    );
    const isNextSameGroup = nextDate !== null && nextDate === currentDate;

    const shouldJoinPrev =
      isSelected &&
      !isCurrentSession &&
      isPrevSelected &&
      !isPrevCurrentSession &&
      !showDateHeader;

    const shouldJoinNext =
      isSelected &&
      !isCurrentSession &&
      isNextSelected &&
      !isNextCurrentSession &&
      isNextSameGroup;

    return (
      <Fragment>
        {showDateHeader && (
          <div className="px-1.5 pb-1 pt-2">
            <TypographyP2 className="text-text-tertiary">{dateHeaderText}</TypographyP2>
          </div>
        )}
        <div
          role="button"
          tabIndex={0}
          data-testid="consolidated-session-list-item"
          data-active={isCurrentSession || undefined}
          className={cn(
            buttonVariants({
              variant: 'panel_ghost',
              disableAutomaticSvgSizing: true,
              disableAutomaticTypographyClasses: true,
              size: 'lg',
            }),
            'group flex h-auto w-full cursor-pointer items-center justify-start gap-2 rounded-md p-1.5 transition-none',
            // Current session
            isCurrentSession && 'font-bold',
            // Adjacency joining for multi-select
            shouldJoinPrev && '-mt-0.5 rounded-t-none',
            shouldJoinNext && 'rounded-b-none'
          )}
          onClick={() => onSessionClick(session)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSessionClick(session);
            }
          }}
        >
          {/* Session time */}
          <TypographyCaption className="w-16 shrink-0 text-text-secondary">
            {displaySessionTime(session.created_time_local, true)}
          </TypographyCaption>

          {/* Patient profile icon + session name */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <SessionUrgencyFlag isUrgent={isSessionUrgent(session)} />
            <ConsolidatedSessionLinkAvatar
              sessionId={session.session_id}
              isSessionLinked={isSessionLinked}
              patientInitials={patientInitials}
              patientProfileAppointmentCandidates={
                session.patient_profile_appointment_candidates
              }
              isOfflineSynthetic={session.is_offline_synthetic}
            />
            <TypographyCaption
              className={cn('truncate', isCurrentSession && 'font-medium')}
            >
              {session.displayName ||
                intl.formatMessage(consolidatedSessionsListMessages.untitledSession)}
            </TypographyCaption>
            {/* Inline offline sync status glyph — reuses the same badge
                vocabulary as the new session list. Renders nothing for an
                ordinary session or for the recorder-owned `recording` state. */}
            <OfflineSyncIndicator
              status={session.offline_sync_status}
              sessionId={session.session_id}
              data-testid="consolidated-session-list-offline-sync-indicator"
            />
          </div>

          {/* Selection indicator area - fixed size-4 container (right side) */}
          <div className="flex size-4 shrink-0 items-center justify-center">
            {/* Status dot - shown when not hovering AND not selected (Together tier only) */}
            {isTogetherOrHigherTier && !isSelected && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex size-4 items-center justify-center group-hover:hidden">
                    <Circle
                      size={6}
                      fill="currentColor"
                      className={cn(statusIconColors[statusKey])}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <TooltipArrow />
                  <TypographyCaption>
                    {intl.formatMessage(statusNameMessages[sessionStatus])}
                  </TypographyCaption>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Checkbox - shown on hover OR when selected. Synthetic
                pre-sync offline rows are never selectable: selecting one
                would flow its local id into the /sessions/batch delete
                path, which targets a non-existent backend session. */}
            {sessionUniqueId && !session.is_offline_synthetic && (
              <div
                className={cn(
                  'items-center justify-center',
                  isSelected ? 'flex' : 'hidden group-hover:flex'
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={(checked) => setSelected(!!checked)}
                />
              </div>
            )}
          </div>
        </div>
      </Fragment>
    );
  }
);

ConsolidatedSessionListItem.displayName = 'ConsolidatedSessionListItem';
